import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, Trash2, Plus, X, Send, Package, PenTool, Wrench, Percent, Printer, ChevronRight, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAT_RATE = 0.15;
const fmt = (n) => Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_META = {
  draft:     { label: 'Draft',     colour: 'bg-gray-500/20 text-gray-300' },
  sent:      { label: 'Sent',      colour: 'bg-blue-500/20 text-blue-300' },
  accepted:  { label: 'Accepted',  colour: 'bg-green-500/20 text-green-300' },
  rejected:  { label: 'Rejected',  colour: 'bg-red-500/20 text-red-300' },
  converted: { label: 'Converted', colour: 'bg-emerald-600/30 text-emerald-300' },
};

const NEXT_STATUSES = {
  draft:     ['sent', 'rejected'],
  sent:      ['accepted', 'rejected', 'draft'],
  accepted:  ['converted', 'rejected'],
  rejected:  ['draft'],
  converted: [],
};

const emptyClient = {
  company: '', contact_person: '', email: '', phone: '', address: '', vat_no: '',
  project_name: '', site_address: '', project_reference: '',
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const TYPE_META = {
  product:  { icon: Package,  label: 'Product',  colour: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
  custom:   { icon: PenTool,  label: 'Custom',   colour: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  service:  { icon: Wrench,   label: 'Service',  colour: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  discount: { icon: Percent,  label: 'Discount', colour: 'bg-red-500/20 text-red-300 border-red-500/40' },
};

const AdminQuotes = ({ products }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  // Builder state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [client, setClient] = useState(emptyClient);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [validDays, setValidDays] = useState(30);
  const [productSearch, setProductSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Product-picker modal state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerBrand, setPickerBrand] = useState('all');
  const [pickerCategory, setPickerCategory] = useState('all');

  useEffect(() => { fetchQuotes(); }, []);

  const fetchQuotes = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, { withCredentials: true });
      setQuotes(data);
    } catch (e) {
      // silent — 401 etc
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setClient(emptyClient);
    setItems([]);
    setNotes('');
    setValidDays(30);
    setProductSearch('');
    setBuilderOpen(true);
  };

  const openEdit = (q) => {
    setEditingId(q.id);
    setClient({ ...emptyClient, ...(q.client || {}) });
    setItems((q.items || []).map((it) => ({
      type: it.type,
      sku: it.sku || null,
      product_name: it.product_name || '',
      description: it.description || '',
      quantity: it.type === 'discount' ? 1 : (it.quantity || 1),
      unit_price_excl: it.type === 'discount' ? Math.abs(it.unit_price_excl || 0) : (it.unit_price_excl || 0),
      rate_type: it.rate_type || (it.type === 'service' ? 'flat' : undefined),
    })));
    setNotes(q.notes || '');
    setValidDays(q.valid_days || 30);
    setProductSearch('');
    setBuilderOpen(true);
  };

  const addProductLine = (p) => {
    setItems([...items, {
      type: 'product',
      sku: p.sku,
      product_name: p.product_name || p.name || p.sku,
      description: '',
      quantity: 1,
      unit_price_excl: Number(p.base_price_excl || p.base_price || 0),
    }]);
    setProductSearch('');
  };
  const addCustomLine = () => setItems([...items, {
    type: 'custom', product_name: '', description: '', quantity: 1, unit_price_excl: 0,
  }]);
  const addServiceLine = () => setItems([...items, {
    type: 'service', product_name: '', description: '', quantity: 1, unit_price_excl: 0, rate_type: 'flat',
  }]);
  const addDiscountLine = () => setItems([...items, {
    type: 'discount', product_name: 'Discount', description: '', quantity: 1, unit_price_excl: 0,
  }]);
  const removeLine = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateLine = (idx, key, val) => setItems(items.map((it, i) => i === idx ? { ...it, [key]: val } : it));

  // Live totals
  const subtotalExcl = items.reduce((acc, it) => {
    const qty = Number(it.quantity) || 0;
    const unit = Number(it.unit_price_excl) || 0;
    if (it.type === 'discount') return acc - Math.abs(unit);
    if (it.type === 'service' && it.rate_type === 'flat') return acc + unit;
    return acc + unit * qty;
  }, 0);
  const vatAmount = subtotalExcl * VAT_RATE;
  const totalIncl = subtotalExcl + vatAmount;

  const validateItems = () => {
    if (items.length === 0) return 'Add at least one line item';
    for (const [i, it] of items.entries()) {
      if (!it.product_name?.trim()) return `Line ${i + 1}: description required`;
      if (it.type === 'product' && !it.sku) return `Line ${i + 1}: product missing SKU`;
      if (it.type !== 'product' && (it.unit_price_excl === '' || Number.isNaN(Number(it.unit_price_excl)))) {
        return `Line ${i + 1}: price required`;
      }
      if (it.type === 'discount' && Number(it.unit_price_excl) <= 0) return `Line ${i + 1}: discount amount must be > 0`;
    }
    if (!client.company?.trim() && !client.project_name?.trim()) return 'Client company OR project name is required';
    return null;
  };

  const buildPayload = () => ({
    client,
    items: items.map((it) => ({
      type: it.type,
      sku: it.sku || null,
      product_name: it.product_name,
      description: it.description || null,
      quantity: Number(it.quantity) || 1,
      unit_price_excl: Number(it.unit_price_excl) || 0,
      rate_type: it.type === 'service' ? (it.rate_type || 'flat') : null,
    })),
    notes: notes || null,
    valid_days: Number(validDays) || 30,
    save: true,
  });

  const saveDraft = async () => {
    const err = validateItems();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/quotes/${editingId}`, buildPayload(), { withCredentials: true });
        toast.success('Quote updated');
      } else {
        await axios.post(`${API}/admin/quotes`, buildPayload(), { withCredentials: true });
        toast.success('Quote saved as draft');
      }
      setBuilderOpen(false);
      fetchQuotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSubmitting(false); }
  };

  const previewPdf = async () => {
    const err = validateItems();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}/admin/quotes/preview-pdf`, buildPayload(), {
        withCredentials: true, responseType: 'blob',
      });
      downloadBlob(new Blob([resp.data], { type: 'application/pdf' }), 'EWSS-Quote-preview.pdf');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setSubmitting(false); }
  };

  const changeStatus = async (q, newStatus) => {
    try {
      await axios.put(`${API}/admin/quotes/${q.id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Marked ${STATUS_META[newStatus]?.label || newStatus}`);
      fetchQuotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status change failed');
    }
  };

  const downloadPdf = async (q) => {
    try {
      const resp = await axios.get(`${API}/admin/quotes/${q.id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const ref = q.quote_number || `DRAFT-${q.id.slice(0, 8).toUpperCase()}`;
      downloadBlob(new Blob([resp.data], { type: 'application/pdf' }), `EWSS-${ref}.pdf`);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Delete quote ${q.quote_number || q.id.slice(0, 8).toUpperCase()}?`)) return;
    try {
      await axios.delete(`${API}/admin/quotes/${q.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchQuotes();
    } catch (e) { toast.error('Delete failed'); }
  };

  const filtered = filterStatus === 'all' ? quotes : quotes.filter((q) => q.status === filterStatus);
  const filteredProducts = products.filter((p) => {
    if (!productSearch) return true;
    const s = productSearch.toLowerCase();
    return (p.sku || '').toLowerCase().includes(s) || (p.product_name || p.name || '').toLowerCase().includes(s);
  });

  // ---- Picker modal derived data --------------------------------------
  const brands = useMemo(() => {
    const set = new Set(products.map((p) => p.brand).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [products]);
  const categories = useMemo(() => {
    const set = new Set(
      products
        .filter((p) => pickerBrand === 'all' || p.brand === pickerBrand)
        .map((p) => p.category)
        .filter(Boolean)
    );
    return ['all', ...Array.from(set).sort()];
  }, [products, pickerBrand]);
  const pickerFiltered = useMemo(() => {
    const s = pickerSearch.toLowerCase().trim();
    return products.filter((p) => {
      if (pickerBrand !== 'all' && p.brand !== pickerBrand) return false;
      if (pickerCategory !== 'all' && p.category !== pickerCategory) return false;
      if (!s) return true;
      return (p.sku || '').toLowerCase().includes(s) || (p.product_name || p.name || '').toLowerCase().includes(s);
    });
  }, [products, pickerSearch, pickerBrand, pickerCategory]);

  const pickProduct = (p) => {
    addProductLine(p);
    toast.success(`Added ${p.sku}`);
  };

  if (loading) return <div className="text-gray-400">Loading quotes…</div>;

  return (
    <div className="space-y-5" data-testid="admin-quotes">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Project Quotes</h2>
          <p className="text-gray-400 text-sm">Build EWSS-branded quotes with products, custom items, services and discounts.</p>
        </div>
        <button onClick={openNew} data-testid="admin-quotes-new-btn"
          className="px-4 py-2 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 flex items-center gap-2">
          <Plus size={16} /> New Project Quote
        </button>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'draft', 'sent', 'accepted', 'converted', 'rejected'].map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)} data-testid={`admin-quotes-filter-${s}`}
            className={`px-3 py-1.5 text-xs rounded-full border ${filterStatus === s
              ? 'bg-ewss-cyan text-ewss-navy border-ewss-cyan font-semibold'
              : 'border-gray-600 text-gray-400 hover:text-white'}`}>
            {s === 'all' ? `All (${quotes.length})` : `${STATUS_META[s]?.label} (${quotes.filter(q => q.status === s).length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-10 text-gray-400 bg-black/20 rounded-xl border border-gray-700">
          No quotes {filterStatus !== 'all' ? `with status "${filterStatus}"` : 'yet'}. Click <b>New Project Quote</b> to start.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((q) => {
            const st = STATUS_META[q.status] || STATUS_META.draft;
            return (
              <div key={q.id} className="bg-black/40 border border-gray-700 rounded-xl p-4" data-testid={`admin-quote-${q.id.slice(0, 8)}`}>
                <div className="flex justify-between items-start flex-wrap gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-bold">{q.quote_number || `DRAFT-${q.id.slice(0, 8).toUpperCase()}`}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded ${st.colour}`}>{st.label}</span>
                    </div>
                    <div className="text-gray-400 text-xs">{new Date(q.created_at).toLocaleString('en-ZA')}</div>
                    <div className="text-gray-300 text-sm mt-1">
                      {q.client?.company && <><b>{q.client.company}</b> · </>}
                      {q.client?.project_name || <span className="text-gray-500 italic">no project name</span>}
                      {q.client?.project_reference && <> · Ref: {q.client.project_reference}</>}
                    </div>
                    <div className="text-gray-500 text-xs mt-1">{q.items?.length || 0} line{q.items?.length === 1 ? '' : 's'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Total (incl VAT)</div>
                    <div className="text-ewss-cyan font-bold text-lg">R {fmt(q.total_incl)}</div>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap mt-3">
                  <button onClick={() => downloadPdf(q)} className="px-3 py-1.5 border border-ewss-cyan/30 text-ewss-cyan text-xs rounded hover:bg-ewss-cyan/10 flex items-center gap-1" data-testid={`admin-quote-pdf-${q.id.slice(0, 8)}`}>
                    <FileDown size={14} /> PDF
                  </button>
                  {q.status !== 'converted' && q.status !== 'accepted' && (
                    <button onClick={() => openEdit(q)} className="px-3 py-1.5 border border-gray-500/40 text-gray-300 text-xs rounded hover:bg-white/5 flex items-center gap-1" data-testid={`admin-quote-edit-${q.id.slice(0, 8)}`}>
                      <PenTool size={14} /> Edit
                    </button>
                  )}
                  {NEXT_STATUSES[q.status]?.map((ns) => (
                    <button key={ns} onClick={() => changeStatus(q, ns)}
                      className="px-3 py-1.5 border border-blue-500/30 text-blue-300 text-xs rounded hover:bg-blue-500/10 flex items-center gap-1"
                      data-testid={`admin-quote-status-${ns}-${q.id.slice(0, 8)}`}>
                      <ChevronRight size={14} /> Mark {STATUS_META[ns]?.label}
                    </button>
                  ))}
                  <button onClick={() => deleteQuote(q)} className="px-3 py-1.5 border border-red-500/30 text-red-400 text-xs rounded hover:bg-red-500/10 flex items-center gap-1" data-testid={`admin-quote-delete-${q.id.slice(0, 8)}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {builderOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="admin-quote-builder">
          <div className="bg-ewss-navy border border-gray-700 rounded-xl max-w-6xl w-full my-6 p-6 space-y-5">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">{editingId ? 'Edit Project Quote' : 'New Project Quote'}</h3>
              <button onClick={() => setBuilderOpen(false)} className="text-gray-400 hover:text-white" data-testid="admin-quote-builder-close">
                <X size={20} />
              </button>
            </div>

            {/* Client + project */}
            <div>
              <div className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Client &amp; Project</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-black/30 rounded-lg border border-gray-700">
                <input placeholder="Client company" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" data-testid="admin-quote-client-company" />
                <input placeholder="Contact person" value={client.contact_person} onChange={(e) => setClient({ ...client, contact_person: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
                <input placeholder="Client email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
                <input placeholder="Phone" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
                <input placeholder="Client address" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm md:col-span-2" />
                <input placeholder="Client VAT number" value={client.vat_no} onChange={(e) => setClient({ ...client, vat_no: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
                <input placeholder="Project name" value={client.project_name} onChange={(e) => setClient({ ...client, project_name: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm border-ewss-cyan/40" data-testid="admin-quote-project-name" />
                <input placeholder="Client project reference" value={client.project_reference} onChange={(e) => setClient({ ...client, project_reference: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
                <input placeholder="Site address" value={client.site_address} onChange={(e) => setClient({ ...client, site_address: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm md:col-span-3" />
              </div>
            </div>

            {/* Add-line toolbar */}
            <div>
              <div className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Add Line Items</div>
              <div className="flex gap-2 flex-wrap mb-3">
                <button onClick={() => setPickerOpen(true)} className="px-4 py-2 bg-ewss-cyan text-ewss-navy font-semibold text-sm rounded hover:bg-ewss-cyan/90 flex items-center gap-2" data-testid="admin-quote-add-product-btn">
                  <Package size={16} /> Add Product from Catalog
                </button>
                <button onClick={addCustomLine} className="px-3 py-2 border border-amber-500/40 text-amber-300 text-sm rounded hover:bg-amber-500/10 flex items-center gap-1" data-testid="admin-quote-add-custom"><PenTool size={14} /> Custom Item</button>
                <button onClick={addServiceLine} className="px-3 py-2 border border-emerald-500/40 text-emerald-300 text-sm rounded hover:bg-emerald-500/10 flex items-center gap-1" data-testid="admin-quote-add-service"><Wrench size={14} /> Service / Labour</button>
                <button onClick={addDiscountLine} className="px-3 py-2 border border-red-500/40 text-red-300 text-sm rounded hover:bg-red-500/10 flex items-center gap-1" data-testid="admin-quote-add-discount"><Percent size={14} /> Discount</button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={14} />
                <input value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
                  placeholder={products.length === 0 ? 'Product catalog is empty on this environment' : 'Quick search — type SKU or product name to add'}
                  disabled={products.length === 0}
                  className="w-full pl-9 pr-3 py-2 bg-black/30 border border-gray-600 rounded text-white text-sm mb-2 disabled:opacity-50"
                  data-testid="admin-quote-product-search" />
              </div>
              {productSearch && products.length > 0 && (
                <div className="max-h-52 overflow-y-auto border border-gray-700 rounded">
                  {filteredProducts.slice(0, 20).map((p) => (
                    <button key={p.sku} onClick={() => addProductLine(p)} className="w-full text-left px-3 py-1.5 border-b border-gray-800 hover:bg-white/5 text-sm flex justify-between text-white" data-testid={`admin-quote-add-product-${p.sku}`}>
                      <span className="truncate mr-2">{p.sku} · {p.product_name || p.name}</span>
                      <span className="text-gray-400 whitespace-nowrap">R {fmt(p.base_price_excl)}</span>
                    </button>
                  ))}
                  {filteredProducts.length === 0 && <div className="p-3 text-gray-400 text-sm">No products match.</div>}
                </div>
              )}
            </div>

            {/* Line items */}
            <div className="space-y-2">
              {items.map((it, idx) => {
                const meta = TYPE_META[it.type];
                const Icon = meta.icon;
                const qty = Number(it.quantity) || 0;
                const unit = Number(it.unit_price_excl) || 0;
                const lineTotal = it.type === 'discount' ? -Math.abs(unit)
                  : it.type === 'service' && it.rate_type === 'flat' ? unit
                  : unit * qty;
                return (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-black/30 p-3 rounded border border-gray-700 text-sm" data-testid={`admin-quote-line-${idx}`}>
                    <div className="col-span-12 md:col-span-4">
                      <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border mb-1 ${meta.colour}`}>
                        <Icon size={11} /> {meta.label}
                        {it.type === 'product' && it.sku && <span className="ml-1 opacity-70">· {it.sku}</span>}
                      </div>
                      <input placeholder="Description" value={it.product_name} onChange={(e) => updateLine(idx, 'product_name', e.target.value)}
                        className="w-full px-2 py-1.5 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
                      <input placeholder="Detail (optional)" value={it.description || ''} onChange={(e) => updateLine(idx, 'description', e.target.value)}
                        className="w-full px-2 py-1 bg-ewss-navy border border-gray-600 rounded text-gray-300 text-xs mt-1" />
                    </div>

                    {it.type === 'service' && (
                      <div className="col-span-6 md:col-span-2">
                        <label className="text-[10px] text-gray-400">Rate type</label>
                        <select value={it.rate_type || 'flat'} onChange={(e) => updateLine(idx, 'rate_type', e.target.value)}
                          className="w-full px-2 py-1.5 bg-ewss-navy border border-gray-600 rounded text-white text-sm" data-testid={`admin-quote-line-rate-${idx}`}>
                          <option value="flat">Flat fee</option>
                          <option value="hourly">Hourly</option>
                        </select>
                      </div>
                    )}

                    {it.type !== 'discount' && (
                      <div className={`col-span-4 md:col-span-${it.type === 'service' ? '1' : '2'}`}>
                        <label className="text-[10px] text-gray-400">
                          {it.type === 'service' && it.rate_type === 'hourly' ? 'Hours' : 'Qty'}
                        </label>
                        <input type="number" min="0" step="0.5" value={it.quantity}
                          onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                          disabled={it.type === 'service' && it.rate_type === 'flat'}
                          className="w-full px-2 py-1.5 bg-ewss-navy border border-gray-600 rounded text-white text-sm disabled:opacity-50" />
                      </div>
                    )}

                    <div className={`col-span-8 md:col-span-${it.type === 'discount' ? '3' : (it.type === 'service' ? '2' : '2')}`}>
                      <label className="text-[10px] text-gray-400">
                        {it.type === 'service' && it.rate_type === 'hourly' ? 'Rate / hr' : it.type === 'discount' ? 'Amount' : 'Unit price'} (excl VAT)
                      </label>
                      <input type="number" min="0" step="0.01" value={it.unit_price_excl}
                        onChange={(e) => updateLine(idx, 'unit_price_excl', e.target.value)}
                        className="w-full px-2 py-1.5 bg-ewss-navy border border-gray-600 rounded text-white text-sm" data-testid={`admin-quote-line-price-${idx}`} />
                    </div>

                    <div className="col-span-8 md:col-span-2 text-right">
                      <label className="text-[10px] text-gray-400">Line total (excl VAT)</label>
                      <div className={`px-2 py-1.5 font-semibold ${lineTotal < 0 ? 'text-red-400' : 'text-ewss-cyan'}`}>
                        R {fmt(lineTotal)}
                      </div>
                    </div>

                    <div className="col-span-4 md:col-span-1 text-right">
                      <button onClick={() => removeLine(idx)} className="text-red-400 hover:bg-red-500/20 p-1 rounded mt-4"><Trash2 size={14} /></button>
                    </div>
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="text-center py-6 text-gray-500 text-sm bg-black/20 rounded border border-gray-800">
                  No lines yet. Search a product above, or click Custom Item / Service / Discount.
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <textarea rows={3} placeholder="Notes / scope statement (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}
                className="md:col-span-2 px-3 py-2 bg-black/30 border border-gray-600 rounded text-white text-sm" />
              <div>
                <label className="text-xs text-gray-400">Valid for (days)</label>
                <input type="number" min="1" max="365" value={validDays} onChange={(e) => setValidDays(e.target.value)}
                  className="w-full px-3 py-2 bg-black/30 border border-gray-600 rounded text-white text-sm" />
              </div>
            </div>

            {/* Totals */}
            <div className="p-4 bg-black/30 rounded-lg border border-ewss-cyan/30 space-y-1 text-sm">
              <div className="flex justify-between text-gray-300"><span>Subtotal (excl VAT)</span><span>R {fmt(subtotalExcl)}</span></div>
              <div className="flex justify-between text-gray-400 text-xs"><span>VAT (15%)</span><span>R {fmt(vatAmount)}</span></div>
              <div className="flex justify-between text-white font-semibold pt-1 border-t border-gray-700"><span>Total (incl VAT)</span><span className="text-ewss-cyan">R {fmt(totalIncl)}</span></div>
            </div>

            <div className="flex justify-end gap-2 flex-wrap">
              <button onClick={previewPdf} disabled={submitting} className="px-5 py-2.5 border border-gray-500 text-gray-200 rounded-lg hover:bg-white/5 flex items-center gap-2 disabled:opacity-50" data-testid="admin-quote-preview-btn">
                <Printer size={16} /> Preview PDF
              </button>
              <button onClick={saveDraft} disabled={submitting} className="px-6 py-2.5 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 disabled:opacity-50 flex items-center gap-2" data-testid="admin-quote-save-btn">
                <Send size={16} /> {submitting ? 'Saving…' : (editingId ? 'Update Quote' : 'Save as Draft')}
              </button>
            </div>
          </div>
        </div>
      )}

      {pickerOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-[60] p-4 overflow-y-auto" data-testid="admin-quote-product-picker">
          <div className="bg-ewss-navy border border-ewss-cyan/40 rounded-xl max-w-5xl w-full my-6 p-6 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2"><Package size={20} /> Add Products from Catalog</h3>
                <p className="text-gray-400 text-xs mt-1">{products.length} products available · click any row to add it to the quote</p>
              </div>
              <button onClick={() => setPickerOpen(false)} className="text-gray-400 hover:text-white" data-testid="admin-quote-product-picker-close">
                <X size={20} />
              </button>
            </div>

            {products.length === 0 ? (
              <div className="text-center py-12 text-gray-400 bg-black/30 border border-gray-700 rounded-lg">
                <Package className="mx-auto mb-3 text-gray-600" size={40} />
                <div className="text-white font-semibold mb-1">Product catalog is empty</div>
                <div className="text-sm">Go to the <b>Products</b> tab and upload your Excel catalog first.</div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="relative md:col-span-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={14} />
                    <input value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} placeholder="Search SKU or product name…"
                      className="w-full pl-9 pr-3 py-2 bg-black/40 border border-gray-600 rounded text-white text-sm" autoFocus
                      data-testid="admin-quote-product-picker-search" />
                  </div>
                  <div className="flex gap-2">
                    <select value={pickerBrand} onChange={(e) => { setPickerBrand(e.target.value); setPickerCategory('all'); }}
                      className="flex-1 px-2 py-2 bg-black/40 border border-gray-600 rounded text-white text-sm" data-testid="admin-quote-product-picker-brand">
                      {brands.map((b) => <option key={b} value={b}>{b === 'all' ? 'All brands' : b}</option>)}
                    </select>
                    <select value={pickerCategory} onChange={(e) => setPickerCategory(e.target.value)}
                      className="flex-1 px-2 py-2 bg-black/40 border border-gray-600 rounded text-white text-sm" data-testid="admin-quote-product-picker-category">
                      {categories.map((c) => <option key={c} value={c}>{c === 'all' ? 'All categories' : c}</option>)}
                    </select>
                  </div>
                </div>

                <div className="border border-gray-700 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-black/40 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                    <div className="col-span-2">SKU</div>
                    <div className="col-span-2">Brand</div>
                    <div className="col-span-5">Product</div>
                    <div className="col-span-2 text-right">List (excl)</div>
                    <div className="col-span-1"></div>
                  </div>
                  <div className="max-h-[55vh] overflow-y-auto">
                    {pickerFiltered.length === 0 && (
                      <div className="p-6 text-center text-gray-400 text-sm">No products match your filters.</div>
                    )}
                    {pickerFiltered.map((p) => {
                      const alreadyAdded = items.some((it) => it.type === 'product' && it.sku === p.sku);
                      return (
                        <button key={p.sku} onClick={() => pickProduct(p)}
                          className="w-full grid grid-cols-12 gap-2 items-center px-3 py-2.5 border-b border-gray-800 hover:bg-ewss-cyan/10 text-left text-sm text-white group"
                          data-testid={`admin-picker-row-${p.sku}`}>
                          <div className="col-span-2 font-mono text-xs text-gray-300">{p.sku}</div>
                          <div className="col-span-2 text-xs text-gray-400">{p.brand}</div>
                          <div className="col-span-5 truncate">{p.product_name || p.name}
                            {p.category && <span className="ml-2 text-[10px] text-gray-500">· {p.category}</span>}
                          </div>
                          <div className="col-span-2 text-right text-ewss-cyan font-semibold">R {fmt(p.base_price_excl)}</div>
                          <div className="col-span-1 text-right">
                            {alreadyAdded
                              ? <span className="text-[10px] text-green-400">Added</span>
                              : <Plus className="ml-auto text-gray-500 group-hover:text-ewss-cyan" size={14} />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-between items-center text-xs text-gray-400">
                  <span>Showing {pickerFiltered.length} of {products.length} products</span>
                  <button onClick={() => setPickerOpen(false)} className="px-4 py-2 bg-ewss-cyan text-ewss-navy font-semibold text-sm rounded hover:bg-ewss-cyan/90" data-testid="admin-quote-product-picker-done">
                    Done ({items.filter((it) => it.type === 'product').length} added)
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuotes;
